import i18next from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/opacity.css";
import PhotoAlbum from "react-photo-album";
import { useParams } from "react-router-dom";
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/plugins/captions.css";
import Download from "yet-another-react-lightbox/plugins/download";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

import Footer from "../components/Footer";
import Header from "../components/Header";
import "./Gallery.css";

const categories = [
  { name: "gallery.landscape", tag: "landscape" },
  { name: "gallery.cities", tag: "city" },
  { name: "gallery.animals", tag: "animal" },
  { name: "gallery.flowers", tag: "flower" },
  { name: "gallery.sunset", tag: "sunset" },
  { name: "gallery.deepSky", tag: "deep-sky" },
];

const sorts = [
  { name: "gallery.sortDefault", sort: "default", funct: (a, b) => 0 },
  {
    name: "gallery.sortTitle",
    sort: "title",
    funct: (a, b) => (a.title < b.title ? -1 : a.title > b.title ? 1 : 0),
  },
  {
    name: "gallery.sortDate",
    sort: "date",
    funct: (a, b) => (a.date && b.date ? b.date - a.date : 0),
  },
  {
    name: "gallery.sortLocation",
    sort: "location",
    funct: (a, b) =>
      a.location && b.location && a.location !== b.location
        ? a.location < b.location
          ? -1
          : a.location > b.location
          ? 1
          : 0
        : b.date - a.date,
  },
];

export default function Gallery({ tag }) {
  const { t } = useTranslation();
  const category = tag ?? useParams()?.category ?? "";

  const [photos, setPhotos] = useState([]);
  const [selection, setSelection] = useState(photos);
  const [filter, setFilter] = useState(category);
  const [search, setSearch] = useState();
  const [sort, setSort] = useState(sorts[0]);
  const [index, setIndex] = useState(-1);

  useEffect(() => {
    fetch("/api/photos?lang=" + i18next.resolvedLanguage)
      .then((response) => response.json())
      .then((data) => setPhotos(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setSelection(
      photos
        .filter(({ tags }) => !filter || filter === "" || tags.includes(filter))
        .filter(
          ({ title, tags }) =>
            !search ||
            search === "" ||
            title.toLowerCase().includes(search.toLowerCase()) ||
            tags.filter((tag) =>
              t("gallery.tag." + tag, tag)
                .toLowerCase()
                .includes(search.toLowerCase())
            ).length
        )
        .toSorted(!sort ? (a, b) => 0 : sort.funct)
    );
  }, [photos, filter, search, sort]);

  return (
    <>
      <Header />

      <main className="gallery">
        <div>
          <div className="bar">
            <h1>{t("gallery.title")}</h1>
            <div className="filter">
              <div
                className={!filter || filter === "" ? "active tag" : "tag"}
                tabIndex={0}
                onClick={() => setFilter("")}
              >
                {t("gallery.all")}
              </div>
              {categories.map((category, index) => (
                <div
                  key={index}
                  className={filter === category.tag ? "active tag" : "tag"}
                  tabIndex={0}
                  onClick={() => setFilter(category.tag)}
                >
                  {t(category.name)}
                </div>
              ))}
              <div className="filter search">
                <input
                  id="search"
                  className="search"
                  placeholder={t("gallery.search")}
                  type="text"
                  onChange={(e) => setSearch(e.target?.value)}
                />
                <div>
                  <div className="dropdown" tabIndex={0}>
                    <div
                      className="option tag"
                      tabIndex={0}
                      onClick={() => {
                        const sort = document.getElementById("sort");
                        sort?.classList.toggle('hidden');
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24"
                        viewBox="0 -960 960 960"
                        width="24"
                      >
                        <path d="M120-240v-80h240v80H120zm0-200v-80h480v80H120zm0-200v-80h720v80H120z" />
                      </svg>
                    </div>
                    <div className="dropdown-content hidden" id="sort">
                      {sorts.map((entry, index) => (
                        <div
                          key={index}
                          className={entry.sort === sort?.sort ? 'active' : undefined}
                          tabIndex={0}
                          onClick={(e) => {
                            setSort(entry);
                            e.target?.parentElement?.classList.toggle('hidden');
                          }}
                        >
                          {t(entry.name)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <PhotoAlbum
            layout="rows"
            photos={selection}
            renderPhoto={GalleryImage}
            spacing={12}
            targetRowHeight={(containerWidth) => {
              if (containerWidth + 40 <= 300) return 1.5 * containerWidth;
              if (containerWidth + 40 <= 600) return containerWidth;
              if (containerWidth + 40 <= 1200) return containerWidth / 2.5;
              if (containerWidth + 40 <= 2400) return containerWidth / 5;
              else return containerWidth / 7.5;
            }}
            onClick={({ index }) => setIndex(index)}
            sizes={{ size: "calc(100vw - 40px)" }}
          />
          <Lightbox
            slides={selection}
            open={index >= 0}
            index={index}
            close={() => setIndex(-1)}
            styles={{
              slide: { padding: 0 },
            }}
            plugins={[Captions, Fullscreen, Zoom, Download, Slideshow]}
          />
        </div>
      </main>

      <Footer />
    </>
  );
}

function GalleryImage({ photo, imageProps, wrapperStyle }) {
  return (
    <div className="image" style={{ ...wrapperStyle }} onClick={imageProps.onClick}>
      <div className="loading">
        <LazyLoadImage
          src={imageProps.src}
          alt={photo.title}
          effect="opacity"
          srcSet={imageProps.srcSet}
          sizes={imageProps.sizes}
          draggable={false}
        />
        <div className="caption">
          <span>{photo.title}</span>
        </div>
      </div>
    </div>
  );
}
